// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/EventsInDates.tsx
// 🔧 PURPOSE: "Nelle tue date · Cosa succede mentre sei qui" della vista B:
//             al massimo tre eventi fra il giorno di arrivo e quello di
//             partenza (lib/homeEvents, eventsInDates), ognuno con la foto
//             (o l'iniziale del paese), il quando ("sab 3 ott · 20:30",
//             l'ora solo se la pagina Eventi l'ha già letta), il titolo e il
//             posto con la distanza da Mola ("Polignano a Mare · a 14 km":
//             il tempo di viaggio non c'è in nessun dato). Tocco → la
//             pagina Eventi con quell'evento in cima (/events?focus=<id>,
//             lib/eventFocus).
//
//             La lista degli eventi copre solo i prossimi 30 giorni: con
//             l'arrivo più lontano non si chiede nemmeno, e si dice che il
//             programma esce circa un mese prima. Sotto, sempre, TUTTI GLI
//             EVENTI.
// ============================================

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { HomeState } from '../../lib/homeState';
import { useEvents, type Event } from '../../contexts/EventsContext';
import { describeEventWhen, eventPlace, eventsInDates, formatEventWhen } from '../../lib/homeEvents';
import { hasEventPhoto } from '../../lib/featuredEvent';
import { eventFocusPath } from '../../lib/eventFocus';
import { PlacePhoto } from '../city/CityPlaces';
import { FOCUS_RING, INK_TEXT, LineButton, MUTED_TEXT, Section, SectionTitle } from './ui';

/** Fin dove arriva la lista degli eventi (EventsContext, ALL_DAYS). */
export const EVENTS_HORIZON_DAYS = 30;
/** Quanti eventi al massimo sulla Home: gli altri sono in /events. */
const MAX_EVENTS = 3;

export interface EventsInDatesProps {
  state: HomeState;
  /** L'ora del clock della Home: "Oggi", "Domani" cambiano con lei. */
  now: Date;
  className?: string;
}

/**
 * La foto dell'evento, o un fondo crema con l'iniziale del paese (come la
 * pagina Eventi). La usa anche "Adesso per te" (NowForYou).
 */
export const EventThumb: React.FC<{ event: Event }> = ({ event }) => (
  <span aria-hidden="true" className="relative h-[72px] w-[72px] flex-shrink-0 overflow-hidden rounded-xl bg-nva-cream dark:bg-stone-800">
    <span className="absolute inset-0 flex items-end justify-end p-1.5 font-display text-[28px] font-bold leading-none text-nva-gold-text/60 dark:text-nva-gold-dark/50">
      {event.city.charAt(0)}
    </span>
    {hasEventPhoto(event) ? <PlacePhoto src={event.imageUrl} /> : null}
  </span>
);

const EventRow: React.FC<{ event: Event; now: Date }> = ({ event, now }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'it';
  return (
    <Link
      to={eventFocusPath(event)}
      className={cn(
        'flex items-center gap-3 rounded-[18px] border border-nva-edge bg-nva-card p-2.5 transition-colors hover:bg-nva-bone/70',
        'dark:border-stone-800 dark:bg-stone-900 dark:hover:bg-stone-800/60',
        FOCUS_RING,
      )}
    >
      <EventThumb event={event} />
      <span className="flex min-w-0 flex-grow flex-col gap-0.5">
        <span className="text-[10.5px] font-extrabold uppercase leading-snug tracking-[0.14em] text-nva-gold-text dark:text-nva-gold-dark">
          {formatEventWhen(describeEventWhen(event, now), t, lang)}
        </span>
        <span className={cn('line-clamp-2 break-words text-[15px] font-bold leading-snug', INK_TEXT)}>{event.title}</span>
        <span className={cn('truncate text-[13px] leading-snug', MUTED_TEXT)}>{eventPlace(event, t)}</span>
      </span>
      <ChevronRight className="h-[18px] w-[18px] flex-shrink-0 text-nva-muted dark:text-stone-500" aria-hidden="true" />
    </Link>
  );
};

const EventsInDates: React.FC<EventsInDatesProps> = ({ state, now, className }) => {
  const { t } = useTranslation();
  const { getAllEvents } = useEvents();
  const titleId = 'home-events-dates-title';
  const from = state.checkInDay;
  const to = state.checkOutDay;
  // Solo se l'arrivo cade nei 30 giorni che la lista conosce.
  const inHorizon = !!from && !!to && state.daysToCheckIn !== null && state.daysToCheckIn <= EVENTS_HORIZON_DAYS;

  const [events, setEvents] = useState<Event[] | null>(null);
  useEffect(() => {
    if (!inHorizon) {
      setEvents(null);
      return undefined;
    }
    let alive = true;
    getAllEvents()
      .then((list) => { if (alive) setEvents(list); })
      .catch(() => { if (alive) setEvents([]); });
    return () => { alive = false; };
  }, [inHorizon, getAllEvents]);

  const list = useMemo(
    () => (events && from && to ? eventsInDates(events, from, to, MAX_EVENTS) : []),
    [events, from, to],
  );

  let body: React.ReactNode;
  if (!inHorizon) {
    body = <p className={cn('m-0 text-[14px] leading-snug', MUTED_TEXT)}>{t('home.before.eventsEmpty')}</p>;
  } else if (events === null) {
    // In attesa della lista: un segnaposto della misura di una riga, così
    // le sezioni sotto non saltano.
    body = (
      <div aria-hidden="true" className="flex items-center gap-3 rounded-[18px] border border-nva-edge bg-nva-card p-2.5 dark:border-stone-800 dark:bg-stone-900">
        <span className="h-[72px] w-[72px] flex-shrink-0 rounded-xl bg-nva-cream motion-safe:animate-pulse dark:bg-stone-800" />
        <span className="flex flex-grow flex-col gap-2">
          <span className="h-2.5 w-24 rounded bg-nva-cream motion-safe:animate-pulse dark:bg-stone-800" />
          <span className="h-3.5 w-40 max-w-full rounded bg-nva-cream motion-safe:animate-pulse dark:bg-stone-800" />
        </span>
      </div>
    );
  } else if (list.length === 0) {
    body = <p className={cn('m-0 text-[14px] leading-snug', MUTED_TEXT)}>{t('home.before.eventsNone')}</p>;
  } else {
    body = (
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {list.map((event) => (
          <li key={event.id}>
            <EventRow event={event} now={now} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Section labelledBy={titleId} className={className}>
      <SectionTitle id={titleId} eyebrow={t('home.before.eventsEyebrow')} title={t('home.before.eventsTitle')} />
      {body}
      <LineButton to="/events" full>{t('home.before.eventsAll')}</LineButton>
    </Section>
  );
};

export default EventsInDates;
